import { z } from 'zod';
import { zStrategySettings } from './strategies.js';
/** Live sends real transactions; paper fills at the exact quoted price and sends nothing. */
export const ExecutionMode = { Paper: 'paper', Live: 'live' };
export const zUpdateExecutionMode = z.object({ mode: z.enum([ExecutionMode.Paper, ExecutionMode.Live]) });
export const TradeTrigger = {
    CopyBuy: 'copy_buy',
    Snipe: 'snipe',
    CopySell: 'copy_sell',
    TakeProfit: 'take_profit',
    InitialsOut: 'initials_out',
    StopLoss: 'stop_loss',
    TrailingStop: 'trailing_stop',
    MaxHold: 'max_hold',
    Manual: 'manual',
    Panic: 'panic',
};
export const TRIGGER_LABEL = {
    copy_buy: 'Copy buy',
    snipe: 'Snipe',
    copy_sell: 'Copy sell',
    take_profit: 'Take profit',
    initials_out: 'Initials out',
    stop_loss: 'Stop loss',
    trailing_stop: 'Trailing stop',
    max_hold: 'Max hold',
    manual: 'Manual',
    panic: 'Panic sell',
};
/** Why the engine did not copy a leader trade. Codes are stable; labels are for people. */
export const SKIP_REASON_LABEL = {
    bot_off: 'Bot is off',
    buys_paused: 'Buys are paused',
    copy_disabled: 'Copying is off for this wallet',
    buy_disabled: 'Copy buys are off',
    sell_disabled: 'Copy sells are off',
    venue_not_allowed: 'Venue not allowed',
    mayhem: 'Mayhem-mode coin',
    blacklisted: 'Token is blacklisted',
    leader_buy_too_small: 'Leader buy below minimum',
    leader_buy_too_large: 'Leader buy above maximum',
    not_first_buy: 'Not the leader’s first buy',
    max_buys_per_token: 'Already bought this token',
    max_position: 'Position is at its maximum',
    market_cap_low: 'Market cap below minimum',
    market_cap_high: 'Market cap above maximum',
    curve_progress_low: 'Curve progress below minimum',
    curve_progress_high: 'Curve progress above maximum',
    /** SOL in the curve or pool (in USD) below `filters.minLiquidityUsd`, or unknown. */
    liquidity_low: 'Liquidity too low',
    /** Our own buy would move the price more than `buy.maxPriceImpactPct` (or it can't be priced). */
    price_impact: 'Price impact too high',
    token_too_new: 'Token too new',
    token_too_old: 'Token too old',
    /** An age filter is set but the coin's creation time couldn't be found in time (fail closed). */
    token_age_unknown: 'Token age unknown',
    /** The test sell (a simulated buy then sell) failed on the sell. */
    cant_sell: 'Can’t sell this token',
    /** The test sell is on but couldn't run (RPC error, no funded payer, unsupported venue). */
    test_sell_unavailable: 'Test sell couldn’t run',
    price_deviation: 'Price moved too far from the leader’s',
    signal_stale: 'Signal arrived too late',
    reserve: 'Would dip below the SOL reserve',
    /** A paper buy needs more paper SOL than the account has left (spend, fees and the reserve). */
    paper_balance: 'Not enough paper SOL',
    /** No longer emitted (older records; an unreadable balance is now `balance_unavailable`). */
    insufficient_balance: 'Not enough SOL',
    /**
     * A live buy's wallet balance, or a paper buy's paper SOL, could not be read, so the buy was
     * skipped (fail closed).
     */
    balance_unavailable: 'Could not read the balance',
    dust: 'Too small to sell after fees',
    max_open_positions: 'Too many open positions',
    daily_spend_cap: 'Daily spend cap reached',
    /** A risk limit is set but its figure could not be read, so the buy was skipped (fail closed). */
    risk_unavailable: 'Could not check a risk limit',
    /** The tracked wallet's own settings don't combine into valid settings, so nothing is copied. */
    invalid_settings: 'Wallet settings are invalid',
    no_position: 'No copied position to sell',
    leader_sell_too_small: 'Leader sell below minimum',
    no_route: 'No route for this token',
    /** Freezable, clawback, taxed, closable or mintable: the skip's detail names which. */
    unsafe_token: 'Unsafe token',
    no_wallet: 'No wallet to trade from',
    maintenance: 'Paused for maintenance',
};
/** A position's own exit rules: the strategy's exit settings without the selling wallet. */
export const zPositionExitPlan = zStrategySettings.shape.exits.omit({ sellWallet: true });
const exitRules = zPositionExitPlan.shape;
/** A partial plan: what is given replaces the position's current rules (arrays whole). */
export const zPositionExitPlanPatch = z.strictObject({
    takeProfit: exitRules.takeProfit.optional(),
    initialsOut: z.strictObject(exitRules.initialsOut.partial().shape).optional(),
    stopLoss: z.strictObject(exitRules.stopLoss.partial().shape).optional(),
    trailingStop: z.strictObject(exitRules.trailingStop.partial().shape).optional(),
    maxHoldMinutes: exitRules.maxHoldMinutes.optional(),
    emergencySlippagePct: exitRules.emergencySlippagePct.optional(),
});
/** PATCH /positions/:id/exits: `plan` is merged over the rules in force; `null` = the strategy's again. */
export const zUpdatePositionExits = z.object({ plan: zPositionExitPlanPatch.nullable() });
export const zSellPosition = z.object({ percent: z.number().min(1).max(100) });
export const zPositionsQuery = z.object({
    status: z.enum(['open', 'closed']).default('open'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Limits on paper SOL, per amount added or reset to and for an account's total. */
export const PAPER_SOL_LIMITS = { min: 0.1, max: 10_000, maxTotal: 1_000_000 };
const zPaperSol = z
    .number()
    .min(PAPER_SOL_LIMITS.min, `At least ${PAPER_SOL_LIMITS.min} SOL`)
    .max(PAPER_SOL_LIMITS.max, `At most ${PAPER_SOL_LIMITS.max.toLocaleString('en-US')} SOL`);
/** Adds paper SOL to the balance. */
export const zPaperDeposit = z.object({ sol: zPaperSol });
/** Starts a new paper session with this much paper SOL. */
export const zPaperReset = z.object({ sol: zPaperSol });
export const zUpdateSystemSettings = z.object({
    platformFeeBps: z.number().int().min(0).max(500).optional(),
    treasury: z.string().trim().min(32).max(44).nullable().optional(),
    signupMode: z.enum(['open', 'closed']).optional(),
    maintenance: z.boolean().optional(),
    paperStartingSol: zPaperSol.optional(),
});
//# sourceMappingURL=trading.js.map